// src/components/research/display.js

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

/* An oversized opening line with one supporting line under it.
   ------------------------------------------------------------------------
   Opens a section the way .big-heading opens the hero: the claim is made at
   display size before any body copy starts, so a reader who reads two lines
   and leaves has still read the claim.

   This replaced a track of six questions that rotated one at a time. The
   rotation was removed for a reason worth keeping written down: a reader
   arriving mid-cycle saw one of the six and the page had no say in which.
   Content the page cannot choose is content the page does not have, and this
   is the one block on the home page whose whole job is to be read.

   The main line never wraps. Broken across two lines it stops reading as one
   statement and starts reading as a headline, so it is sized against the
   viewport with a cap and shrinks to fit instead. */
const StyledDisplay = styled.div`
  margin: 0 0 var(--space-6);
  text-align: center;

  .main {
    margin: 0;
    color: var(--accent);
    font-family: var(--font-sans);
    /* A line of about 32 characters is ~13.3em wide in this face at this
       weight, and the binding case is not the narrowest viewport: the page
       gutter steps from 25px to 50px a side at 481px, so the column loses
       50px while the viewport gains one. 5.6vw clears that step with room and
       still reaches the 34px cap by ~607px. Raising it means re-checking just
       above 480, not at 320. */
    font-size: min(5.6vw, 34px);
    font-weight: 600;
    line-height: var(--lh-title);
    letter-spacing: -0.02em;
    white-space: nowrap;
  }

  /* Accented, and two steps below the body copy under it. Both lines carry
     the accent so they read as one object rather than as a statement with a
     caption attached, and the size gap is what keeps them from competing:
     at 34px against 14px nothing has to be dimmed for the order to be
     obvious. At equal size the same pair would read as two headings. */
  .sub {
    max-width: var(--measure);
    margin: var(--space-3) auto 0;
    color: var(--accent);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    line-height: var(--lh-body);
  }
`;

const Display = ({ sub, children }) => (
  <StyledDisplay>
    <p className="main">{children}</p>
    {sub ? <p className="sub">{sub}</p> : null}
  </StyledDisplay>
);

Display.propTypes = {
  sub: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default Display;
